import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GuestAwareLink, AuthContext } from '../AuthContext';
import { getAuth, signOut } from 'firebase/auth';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import '../css/Navigation.css';
import bibliobingohedgehog_rightquilt from '../bibliobingohedgehog_rightquilt.png';
import BlogFeed from './BlogFeed';

const LogoutIcon = ({ onClick }) => (
    <button className="logout-icon-btn" onClick={onClick} title="Log out" aria-label="Log out">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6B705C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
    </button>
);

const Navigation = () => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const { user } = useContext(AuthContext);
    const auth = getAuth();
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [blogFilter, setBlogFilter] = useState('all');

    const toggleMenu = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const closeMenu = useCallback(() => {
        setIsOpen(false);
    }, []);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.nav-menu') && !event.target.closest('.hamburger-menu')) {
                closeMenu();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, closeMenu]);

    // Close menu when route changes
    useEffect(() => {
        closeMenu();
    }, [location.pathname, closeMenu]);

    useEffect(() => {
        if (isOpen) {
            setRefreshTrigger(t => t + 1);
        }
    }, [isOpen]);

    const handleLogOut = () => {
        signOut(auth)
            .then(() => {
                showSuccessToast('Logged out successfully!');
                window.location.href = '/login';
            })
            .catch(() => {
                showErrorToast('Failed to log out. Please try again.');
            });
    };

    return (
        <div className="nav-container">
            <button className={`hamburger-menu ${isOpen ? 'open' : ''}`} onClick={toggleMenu}>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
            </button>

            <nav className={`nav-menu ${isOpen ? 'open' : ''}`}>
                <div className="logo-container">
                    <img src={bibliobingohedgehog_rightquilt} alt="Book Bingo Logo" />
                    <h1>Book Bingo</h1>
                </div>
                <ul>
                    <li>
                        <Link 
                            to="/bingo"
                            className={location.pathname === '/bingo' ? 'active' : ''}
                        >
                            Bingo
                        </Link>
                    </li>
                    <li>
                        <Link 
                            to="/rules" 
                            className={location.pathname === '/rules' ? 'active' : ''}
                        >
                            Rules
                        </Link>
                    </li>
                    <li>
                        <GuestAwareLink 
                            to="/profile"
                            className={location.pathname === '/profile' ? 'active' : ''}
                        >
                            Profile
                        </GuestAwareLink>
                    </li>
                    <li>
                        <GuestAwareLink 
                            to="/recommendations"
                            className={location.pathname === '/recommendations' ? 'active' : ''}
                        >
                            Book Recommendations
                        </GuestAwareLink>
                    </li>
                    {user && (
                        <li className="logout-list-item">
                            <LogoutIcon onClick={handleLogOut} />
                        </li>
                    )}
                </ul>
                <hr className="nav-divider" />
                <div className="blog-section-header">
                    <span className="blog-title">Blog</span>
                    <select
                        className="blog-filter-dropdown"
                        value={blogFilter}
                        onChange={e => setBlogFilter(e.target.value)}
                        aria-label="Filter blog posts"
                    >
                        <option value="all">All Posts</option>
                        <option value="friends">Friends' Posts</option>
                        <option value="mine">My Posts</option>
                    </select>
                </div>
                <BlogFeed refreshTrigger={refreshTrigger} filter={blogFilter} />
            </nav>
        </div>
    );
};

export default Navigation; 